import html2canvas from "html2canvas";
import { makeAutoObservable, toJS } from "mobx";
import { MutableRefObject } from "react";

import RootContext from "@client/context/RootContext";
import { inspectJSON } from "@client/helpers/utils";
import { useIsTouchDevice } from "@client/hooks/useIsTouchDevice";

export class PrintStore {
	orderReference = "";

	private printingModals?: any;

	constructor(private rootContext: RootContext) {
		makeAutoObservable(this);
	}

	public initialise = async () => {
		if (!process.env.IS_SERVER) {
			await import("@client/helpers/printing-modals").then(
				(PrintingModals) =>
					(this.printingModals = new PrintingModals.default(this.rootContext))
			);
		}
	};

	public openPrintingModal = (
		timeout?: number | string,
		printAgain?: boolean
	) => {
		const isTouchDevice = useIsTouchDevice();
		if (!isTouchDevice) {
			return;
		}
		this.printingModals?.openPrintingModal(timeout, printAgain);
	};

	public printShippingSignature = async (
		modalRef: MutableRefObject<HTMLDivElement>,
		orientation: "landscape" | "portrait"
	) => {
		this.openPrintingModal(Number(process.env.REACT_APP_TIMEOUT_PRINT), false);

		const modal = modalRef.current;
		if (!modal) return;

		try {
			const isTouchDevice = useIsTouchDevice();
			if (!isTouchDevice) {
				// print for desktop browsers
				window.print();

				return;
			}

			// print for IOS app — scale:1 avoids retina bloat, JPEG is far smaller than PNG
			const captured = await html2canvas(modal, {
				backgroundColor: "#f1f1f1",
				scale: 1,
			});
			const image = captured.toDataURL("image/jpeg", 0.85);

			const printerSettings =
				toJS(this.rootContext.vendorStore.vendorDetails?.printer) || {};

			printerSettings.print_orientation = orientation;
			const [pdf_username, pdf_password] =
				process.env.REACT_APP_MAGE_DOMAIN_CREDENTIALS?.split(":") || [];
			const webViewMessage = {
				type: "image",
				image,
				pdf_username,
				pdf_password,
				...printerSettings,
			};

			window.webkit?.messageHandlers?.printMessageHandler?.postMessage(
				webViewMessage
			);
		} catch (e) {
			const err = (e as object) || {};
			this.printingModals?.donePrinting(
				"Could not print",
				!window.webkit?.messageHandlers?.printMessageHandler?.postMessage
					? "Are you using the iPad app?"
					: "Please try again later.",
				inspectJSON(err)
				// true
			);
		}
	};

	public printShippingLabel = async (orderReference: string) => {
		this.orderReference = orderReference;
		this.openPrintingModal();

		try {
			const pdfRes = await this.rootContext.apiService.api("/print-label", {
				orderReference,
				print_products:
					this.rootContext.vendorStore.vendorDetails?.print_products == "yes"
						? "1"
						: "0",
			});

			const pdf = pdfRes.data;
			if (!pdf) {
				throw "The generated shipping label is empty.\r\n\r\nIf the problem persists, please contact support.";
			}

			const isTouchDevice = useIsTouchDevice();
			if (!isTouchDevice) {
				window.open(pdf);
				return;
			}

			const printerSettings =
				toJS(this.rootContext.vendorStore.vendorDetails?.printer) || {};

			const [pdf_username, pdf_password] =
				process.env.REACT_APP_MAGE_DOMAIN_CREDENTIALS?.split(":") || [];
			const webViewMessage = {
				pdf_username,
				pdf_password,
				pdf,
				...printerSettings,
			};

			window.webkit?.messageHandlers?.printMessageHandler?.postMessage(
				webViewMessage
			);
		} catch (e) {
			const err = (e as object) || {};
			this.printingModals?.donePrinting(
				"Could not print",
				!window.webkit?.messageHandlers?.printMessageHandler?.postMessage
					? "Are you using the iPad app?"
					: "Please try again later.",
				inspectJSON(err),
				true
			);
		}
	};
}
